// Top strip. Shows the live "now" state and, when a selection is active,
// surfaces the selected entity's quick details + clear-selection button.
//
// This is the single glance read for the dispatcher: clock, bank
// progress, key counts, and "what am I currently looking at."

import React from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FlightLandIcon from '@mui/icons-material/FlightLand';
import GroupsIcon from '@mui/icons-material/Groups';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import ScreenRotationIcon from '@mui/icons-material/ScreenRotation';
import { useIntel } from './IntelligenceContext';
import { liveCounts, DRIVER_BY_ID, GATE_BY_ID, GATE_STATE } from './mockLiveState';
import AlertsBell from './AlertsBell';
import CommsBell from './CommsBell';
import HandoffButton from './HandoffBrief';
import { ReplayButton } from './ReplayScrubber';
import SearchBar from './SearchBar';
import { C } from '../styles';

function fmtClock(mins) {
  const h = Math.floor(mins / 60) % 24;
  const m = Math.floor(mins) % 60;
  const s = Math.floor((mins * 60) % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function ContextBar() {
  const intel = useIntel();
  const { selected, clearSelection, demoTime, mapOrientation, toggleMapOrientation } = intel;

  const counts = React.useMemo(() => liveCounts(), []);

  // Bank progress — current bank window 07:30 → 09:30 (placeholder)
  const BANK_START = 7 * 60 + 30;
  const BANK_END   = 9 * 60 + 30;
  const bankPct = Math.max(0, Math.min(100, ((demoTime - BANK_START) / (BANK_END - BANK_START)) * 100));

  return (
    <Box sx={{
      flexShrink: 0,
      display: 'flex', alignItems: 'stretch',
      borderBottom: `1px solid ${C.border}`,
      backgroundColor: C.surface,
      backdropFilter: C.blur,
      WebkitBackdropFilter: C.blur,
      minHeight: 56,
      // Stacking: the bell-rendered popovers (alerts + comms) live
      // inside this bar but visually drop down over the map. Without
      // an explicit stacking context here, the map's positioned
      // descendants (gate cards, workspace, etc.) end up painted on
      // top because they come later in DOM order. Giving the bar its
      // own context + a high z-index keeps the popovers on top.
      position: 'relative',
      zIndex: 50,
    }}>
      {/* Live clock + bank progress */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 2,
        px: 2, borderRight: `1px solid ${C.border}`,
        minWidth: 280,
      }}>
        <Box>
          <Typography sx={{ fontSize: '0.55rem', color: C.textMuted, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>
            Local
          </Typography>
          <Typography sx={{
            fontSize: '1.4rem', color: C.textPrimary, fontWeight: 700,
            fontVariantNumeric: 'tabular-nums', lineHeight: 1, letterSpacing: 1,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          }}>
            {fmtClock(demoTime)}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minWidth: 100 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.4 }}>
            <Typography sx={{ fontSize: '0.55rem', color: C.textMuted, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>
              Morning Bank
            </Typography>
            <Typography sx={{ fontSize: '0.55rem', color: C.textSecondary, fontWeight: 600 }}>
              07:30 → 09:30
            </Typography>
          </Box>
          <Box sx={{ height: 4, borderRadius: 2, backgroundColor: 'rgba(255, 255, 255, 0.08)', overflow: 'hidden' }}>
            <Box sx={{
              width: `${bankPct}%`, height: '100%',
              backgroundColor: bankPct > 0 && bankPct < 100 ? C.accent : '#4CAF50',
              transition: 'width 1s linear',
            }} />
          </Box>
        </Box>
      </Box>

      {/* Driver counts */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        px: 2, borderRight: `1px solid ${C.border}`,
      }}>
        <GroupsIcon sx={{ fontSize: 16, color: C.textMuted }} />
        <Stat label="In Run"   value={counts.inRun}   color="#00BCD4" />
        <Stat label="At Gate"  value={counts.atGate}  color="#00BCD4" />
        <Stat label="Idle"     value={counts.onShift} color="#94A3B8" />
        <Stat label="Break"    value={counts.onBreak + counts.onLunch} color="#9C27B0" />
      </Box>

      {/* Gate counts */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        px: 2, borderRight: `1px solid ${C.border}`,
      }}>
        <FlightLandIcon sx={{ fontSize: 16, color: C.textMuted }} />
        <Stat label="Parked"   value={counts.gatesParked}   color="#4CAF50" />
        <Stat label="Arriving" value={counts.gatesArriving} color="#2196F3" />
        <Stat label="Pushing"  value={counts.gatesPushing}  color="#F6530F" />
      </Box>

      {/* Search bar — sits right after the gate counts ("9 PUSHING") */}
      <Box sx={{
        display: 'flex', alignItems: 'center', flex: 1,
        px: 2, minWidth: 0, justifyContent: 'flex-start',
      }}>
        <SearchBar />
      </Box>

      {/* Selection display */}
      {selected && (
        <SelectionPill selected={selected} onClear={clearSelection} />
      )}

      {/* Comms + Alerts bells + Orientation + Replay + Handoff */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, borderLeft: `1px solid ${C.border}` }}>
        <CommsBell />
        <AlertsBell />
        <Tooltip title={`Switch map to ${mapOrientation === 'landscape' ? 'portrait' : 'landscape'}`}>
          <IconButton
            size="small"
            onClick={toggleMapOrientation}
            sx={{
              color: mapOrientation === 'portrait' ? C.accent : C.textSecondary,
              backgroundColor: mapOrientation === 'portrait' ? 'rgba(249, 182, 18, 0.10)' : 'transparent',
              '&:hover': { color: C.accent, backgroundColor: 'rgba(249, 182, 18, 0.10)' },
            }}
          >
            <ScreenRotationIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <ReplayButton />
        <HandoffButton />
      </Box>
    </Box>
  );
}

function Stat({ label, value, color }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.4 }}>
      <Typography sx={{ fontSize: '0.95rem', color, fontWeight: 700, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
      <Typography sx={{ fontSize: '0.55rem', color: C.textMuted, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>
        {label}
      </Typography>
    </Box>
  );
}

function SelectionPill({ selected, onClear }) {
  let title, subtitle, color = C.accent;
  if (selected.kind === 'driver') {
    const d = DRIVER_BY_ID.get(selected.id);
    title = d ? d.name : selected.id;
    subtitle = d ? `Driver · #${d.badge} · ${d.status.replace('_', ' ')}` : 'Driver';
  } else if (selected.kind === 'gate') {
    const g = GATE_BY_ID.get(selected.id);
    const s = GATE_STATE.get(selected.id);
    title = g ? g.id : selected.id;
    subtitle = `Gate · ${s?.state || 'unknown'}${s?.flightNumber ? ` · ${s.flightNumber}` : ''}`;
  } else if (selected.kind === 'flight') {
    title = selected.id;
    subtitle = 'Flight';
  }

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1,
      px: 2,
      backgroundColor: 'rgba(249, 182, 18, 0.08)',
      borderLeft: `3px solid ${color}`,
    }}>
      <GpsFixedIcon sx={{ fontSize: 14, color }} />
      <Box>
        <Typography sx={{ fontSize: '0.55rem', color: C.textMuted, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>
          Focused
        </Typography>
        <Typography sx={{ fontSize: '0.85rem', color: C.textPrimary, fontWeight: 700, lineHeight: 1.1 }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: '0.6rem', color: C.textSecondary }}>
          {subtitle}
        </Typography>
      </Box>
      <Tooltip title="Clear selection">
        <IconButton size="small" onClick={onClear} sx={{ color: C.textMuted, '&:hover': { color: '#f44336' } }}>
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

export default ContextBar;
